import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    CheckCircle2, XCircle, KeyRound, QrCode, Trash2, Printer, ShieldOff, Mail,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const ROLES = ["pubblico", "trade", "vip", "staff", "admin"];

export default function AdminUsers() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [staffPanel, setStaffPanel] = useState(null);   // user obj currently opened in staff panel
    const [pinInput, setPinInput] = useState("");
    const [freshQr, setFreshQr] = useState(null);         // { user_id, token, url }
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/users")
            .then(({ data }) => setItems(data.items))
            .catch(() => toast.error("Errore caricamento utenti"))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const changeRole = async (userId, role) => {
        try {
            await api.put(`/admin/users/${userId}/role`, { role });
            toast.success("Ruolo aggiornato");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    const setPin = async (userId) => {
        if (!/^\d{4}$/.test(pinInput)) { toast.error("PIN 4 cifre"); return; }
        try {
            await api.put(`/admin/users/${userId}/staff-pin`, { pin: pinInput });
            toast.success("PIN aggiornato");
            setPinInput(""); setStaffPanel(null); load();
        } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    };
    const clearPin = async (userId) => {
        try {
            await api.delete(`/admin/users/${userId}/staff-pin`);
            toast.success("PIN rimosso");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    };
    const genQr = async (userId) => {
        try {
            const { data } = await api.post(`/admin/users/${userId}/staff-qr/generate`);
            setFreshQr({ user_id: userId, ...data });
            toast.success("QR generato");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    };
    const revokeQr = async (userId) => {
        if (!window.confirm("Revocare il QR attivo? Il vecchio codice smetterà di funzionare.")) return;
        try {
            await api.post(`/admin/users/${userId}/staff-qr/revoke`);
            setFreshQr(null); toast.success("QR revocato"); load();
        } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    };
    const printQr = (userId) => {
        api.get(`/admin/users/${userId}/staff-qr/print`, { responseType: "text" })
            .then((r) => {
                const w = window.open("", "_blank");
                if (w) { w.document.write(r.data); w.document.close(); }
            })
            .catch((err) => toast.error(err.response?.data?.detail || "Errore"));
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/users/${deleteTarget.id}`);
            toast.success("Utente cancellato");
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div data-testid="admin-users-page">
            <h1 className="font-serif text-3xl mb-2">Gestione Utenti</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-2xl">
                Ruoli, verifica email, consensi (privacy e marketing) e strumenti staff (PIN / QR).
                Il pulsante di cancellazione applica il diritto all'oblio GDPR: i dati personali
                vengono rimossi e una traccia anonimizzata resta nel registro cancellazioni.
            </p>

            {freshQr && (
                <div className="mb-4 p-3 rounded-sm bg-yellow-50 border border-yellow-300 text-sm" data-testid="admin-fresh-qr-banner">
                    <div className="font-medium">Nuovo QR staff generato</div>
                    <div className="mt-1 font-mono text-xs break-all">{freshQr.url}</div>
                    <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                        Il vecchio QR è ora invalido. Stampa/apri l'URL sopra o usa il bottone stampa nella riga.
                    </div>
                </div>
            )}

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Ruolo</TableHead>
                            <TableHead>Verificato</TableHead>
                            <TableHead>Consensi</TableHead>
                            <TableHead>Staff · PIN/QR</TableHead>
                            <TableHead>Registrato</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={7} className="text-center py-8 text-[hsl(var(--muted-foreground))]">Caricamento…</TableCell></TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={7} className="text-center py-8 text-[hsl(var(--muted-foreground))]">Nessun utente</TableCell></TableRow>
                        )}
                        {items.map((u) => (
                            <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                                <TableCell className="font-medium text-sm">{u.email}</TableCell>
                                <TableCell>
                                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                                        <SelectTrigger className="w-32 bg-white" data-testid={`admin-user-role-${u.id}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    {u.email_verified
                                        ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                                        : <XCircle className="w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />}
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-col gap-1 text-[10px]">
                                        <span className={`co-badge-neutral ${u.privacy_accepted ? "" : "opacity-40"}`} data-testid={`admin-user-privacy-${u.id}`}>
                                            Privacy {u.privacy_accepted ? "OK" : "—"}
                                        </span>
                                        <span className={`co-badge-neutral inline-flex items-center gap-1 ${u.marketing_consent ? "" : "opacity-40"}`} data-testid={`admin-user-marketing-${u.id}`}>
                                            <Mail className="w-2.5 h-2.5" strokeWidth={1.5} />
                                            Marketing {u.marketing_consent ? "OK" : "—"}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {u.role === "staff" ? (
                                        <div className="flex gap-1 flex-wrap items-center">
                                            <span className={`co-badge-neutral text-[10px] ${u.staff_pin_hash ? "" : "opacity-40"}`}>PIN {u.staff_pin_hash ? "OK" : "—"}</span>
                                            <span className={`co-badge-neutral text-[10px] ${u.staff_qr_token ? "" : "opacity-40"}`}>QR {u.staff_qr_token ? "OK" : "—"}</span>
                                            <button onClick={() => { setStaffPanel(u); setPinInput(""); }} className="co-btn-ghost text-xs !py-1" data-testid={`admin-user-pin-${u.id}`}>
                                                <KeyRound className="w-3 h-3" /> PIN
                                            </button>
                                            <button onClick={() => genQr(u.id)} className="co-btn-ghost text-xs !py-1" data-testid={`admin-user-qr-gen-${u.id}`}>
                                                <QrCode className="w-3 h-3" /> {u.staff_qr_token ? "Ruota QR" : "Genera QR"}
                                            </button>
                                            {u.staff_qr_token && (
                                                <>
                                                    <button onClick={() => printQr(u.id)} className="co-btn-ghost text-xs !py-1" data-testid={`admin-user-qr-print-${u.id}`}>
                                                        <Printer className="w-3 h-3" /> Stampa
                                                    </button>
                                                    <button onClick={() => revokeQr(u.id)} className="co-btn-ghost text-xs !py-1 text-[hsl(var(--destructive))]" data-testid={`admin-user-qr-revoke-${u.id}`}>
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </>
                                            )}
                                            {u.staff_pin_hash && (
                                                <button onClick={() => clearPin(u.id)} className="co-btn-ghost text-xs !py-1 text-[hsl(var(--destructive))]" data-testid={`admin-user-pin-clear-${u.id}`} title="Rimuovi PIN">
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-xs text-[hsl(var(--muted-foreground))]">n/a</span>
                                    )}
                                </TableCell>
                                <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">
                                    {typeof u.created_at === "string" ? u.created_at.slice(0, 10) : "—"}
                                </TableCell>
                                <TableCell className="text-right">
                                    {u.role !== "admin" ? (
                                        <button
                                            onClick={() => setDeleteTarget(u)}
                                            className="co-btn-ghost text-xs !py-1 text-[hsl(var(--destructive))] inline-flex items-center gap-1"
                                            data-testid={`admin-user-delete-${u.id}`}
                                            title="Cancella account (GDPR)"
                                        >
                                            <ShieldOff className="w-3 h-3" strokeWidth={1.5} /> Cancella
                                        </button>
                                    ) : (
                                        <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {staffPanel && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setStaffPanel(null)}>
                    <div className="bg-white rounded-sm max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="admin-set-pin-modal">
                        <h2 className="text-lg mb-1">Imposta PIN staff</h2>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{staffPanel.email}</div>
                        <Input
                            inputMode="numeric"
                            maxLength={4}
                            value={pinInput}
                            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="4 cifre"
                            className="text-center text-xl tracking-[.4em]"
                            data-testid="admin-set-pin-input"
                        />
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => setStaffPanel(null)} className="co-btn-ghost text-sm">Annulla</button>
                            <button onClick={() => setPin(staffPanel.id)} className="co-btn-primary text-sm" data-testid="admin-set-pin-submit" disabled={pinInput.length !== 4}>
                                Salva
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="admin-delete-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancellare l'utente?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="block mb-2">
                                Stai per cancellare definitivamente <b>{deleteTarget?.email}</b>.
                            </span>
                            Verranno rimossi: profilo, PIN/QR, token di verifica e reset. Le email inviate
                            resteranno anonimizzate come traccia operativa. L'utente riceverà una email di
                            conferma. L'operazione è irreversibile.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-delete-user-cancel">Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                            disabled={deleting}
                            className="bg-[hsl(var(--destructive))] hover:opacity-90"
                            data-testid="admin-delete-user-confirm"
                        >
                            {deleting ? "Cancellazione…" : "Sì, cancella"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
